import Switch from '@shared/components/Switch/SwitchButton';
import { useSubscriptionPlan } from '@shared/store';
import { isSubscriptionExpert } from '@shared/entity';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import SanitizerIcon from '@mui/icons-material/Sanitizer';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import { Chip, Grid } from '@mui/material';
import { MutateOptions } from '@tanstack/react-query';
import { ReactNode, useRef } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import CaptionCard from '@shared/components/CaptionCard';
import DetailColumn from '@shared/components/DetailColumn';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { Service } from '../../service/entity';
import AppTheme from '@libs/mui/AppTheme';
import { useServicesQuery } from '../../service/queries';
import { useFormatCurrency } from '@features/currency/currency.hooks';
import { ClientWithVisitsWithVisitServices, PartialClient } from '../entity';
import { useCreateNewOrUpdateClientMutation } from '../queries';
import ClientProfileActions from './client-contact';
import ClientFormDialog from './client-form/client-form-dialog';

type ClientProfileGridProps = {
  clientData: ClientWithVisitsWithVisitServices;
};

const ClientProfileGrid = (props: ClientProfileGridProps) => {
  const { clientData } = props;
  const intl = useIntl();
  const dialogRef = useRef<ImperativeFormDialogRefType | null>(null);
  const { format: fmtCurrency } = useFormatCurrency();
  const earnedMoneyPerClientServices = clientData.visits.reduce(
    (prev, curr) => prev + Number(curr.paidPrice) - Number(curr.loyaltyPointsUsed),
    0,
  );
  const earnedMoneyPerClientProducts = clientData.visits.reduce(
    (prev, curr) => prev + Number(curr.totalSalesRevenue),
    0,
  );

  const visitCount = clientData.visits.length;
  const regularClient = clientData.regularClient;
  const { mutate: changeClientDepositStatus } = useCreateNewOrUpdateClientMutation();
  const { data } = useServicesQuery();
  const plan = useSubscriptionPlan();
  const isExpertPlan = isSubscriptionExpert(plan);

  const allowedServices = data
    ? data.filter(
        service =>
          clientData.allowedServices &&
          clientData.allowedServices.length > 0 &&
          clientData.allowedServices.includes(service.id),
      )
    : [];

  const detailColumns = getDetailColumnConfigs(
    intl,
    earnedMoneyPerClientServices,
    earnedMoneyPerClientProducts,
    visitCount,
    regularClient,
    clientData,
    isExpertPlan,
    fmtCurrency,
  );

  const captionCards = getCaptionCardConfigs(
    intl,
    clientData,
    allowedServices,
    changeClientDepositStatus,
    isExpertPlan,
  );

  return (
    <>
      <ClientFormDialog
        dialogRef={dialogRef}
        defaultValues={{
          firstName: clientData.firstName,
          lastName: clientData.lastName,
          phone: clientData.phone,
          phonePrefix: clientData.phonePrefix,
          note: clientData.note,
          regularClient: clientData.regularClient,
          allowedServices: clientData.allowedServices,
        }}
        clientId={clientData.id}
        enableAllowedServices={isExpertPlan}
      />
      <ClientProfileActions dialogRef={dialogRef} clientData={clientData} />
      <Grid container rowSpacing={2} columnSpacing={1}>
        {detailColumns.map(column => {
          if (!column.shouldDisplay) {
            return null;
          }

          const detailColumnElement = (
            <DetailColumn
              disabled={Boolean(column.disabled)}
              disabledMessage={column.disabledMessage}
              label={column.label}
              input={column.input}
              icon={column.icon}
              iconBackgroundColor={column.iconBackgroundColor}
              onClick={
                column.editable && column.fieldName
                  ? () => dialogRef.current?.open(column.fieldName)
                  : undefined
              }
            />
          );

          return (
            <Grid key={column.id} size={column.gridSize}>
              {detailColumnElement}
            </Grid>
          );
        })}

        {captionCards.map(card => {
          if (card.shouldDisplay === false) {
            return null;
          }

          return (
            <Grid key={card.id} size={12} alignContent="center" justifyContent="center">
              <CaptionCard
                disabled={Boolean(card.disabled)}
                disabledMessage={card.disabledMessage}
                endElement={card.endElement}
                title={card.title}
                caption={card.caption}
                onClick={
                  card.fieldName && card.editable
                    ? () => dialogRef.current?.open(card.fieldName)
                    : undefined
                }
              />
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default ClientProfileGrid;

type DetailColumnConfig = {
  id: string;
  label: string;
  input: string | number;
  icon?: ReactNode;
  iconBackgroundColor?: string;
  gridSize: number;
  shouldDisplay?: boolean;
  fieldName?: keyof ClientWithVisitsWithVisitServices;
  editable?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
};

const getDetailColumnConfigs = (
  intl: IntlShape,
  earnedMoneyPerClientServices: number,
  earnedMoneyPerClientProducts: number,
  visitCount: number,
  regularClient: boolean,
  clientData: ClientWithVisitsWithVisitServices,
  isExpertPlan: boolean,
  fmtCurrency: (amount: number | string | undefined) => string,
): DetailColumnConfig[] => [
  {
    id: 'revenueServices',
    label: intl.formatMessage({
      defaultMessage: 'Tržby služby',
      id: 'clientProfile.totalRevenueServices',
    }),
    input: fmtCurrency(earnedMoneyPerClientServices),
    icon: <AttachMoneyIcon fontSize="small" />,
    iconBackgroundColor: '#69eeff73',
    gridSize: 6,
    shouldDisplay: true,
  },
  {
    id: 'revenueProducts',
    label: intl.formatMessage({
      defaultMessage: 'Tržby produkty',
      id: 'clientProfile.totalRevenueProducts',
    }),
    input: fmtCurrency(earnedMoneyPerClientProducts),
    icon: <SanitizerIcon fontSize="small" color="primary" />,
    iconBackgroundColor: AppTheme.palette.primary.light,
    gridSize: 6,
    shouldDisplay: true,
    disabled: !isExpertPlan,
    disabledMessage: intl.formatMessage({
      defaultMessage: 'Dostupné v plánu EXPERT. Aktivujte ve vašem profilu.',
      id: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    }),
  },
  {
    id: 'visitCount',
    label: intl.formatMessage({
      id: 'clientProfile.totalVisits',
    }),
    input: visitCount,
    icon: <HistoryToggleOffIcon fontSize="small" color="info" />,
    iconBackgroundColor: AppTheme.palette.info.light,
    gridSize: 4,
    shouldDisplay: true,
  },
  {
    id: 'regularClient',
    label: intl.formatMessage({
      defaultMessage: 'Stálý klient',
      id: 'clientProfile.regularClient',
    }),
    input: regularClient ? 'Ano' : 'Ne',
    icon: <TransferWithinAStationIcon fontSize="small" color="secondary" />,
    iconBackgroundColor: AppTheme.palette.secondary.light,
    gridSize: 4,
    editable: true,
    fieldName: 'regularClient',
    shouldDisplay: true,
  },
  {
    id: 'loyaltyPoints',
    label: intl.formatMessage({
      defaultMessage: 'Věrnost',
      id: 'clientProfile.loyaltyPoints',
    }),
    input: `${clientData.loyaltyPoints} bodů`,
    icon: <LoyaltyIcon fontSize="small" sx={{ color: AppTheme.palette.warning.main }} />,
    iconBackgroundColor: AppTheme.palette.warning.light,
    gridSize: 4,
    shouldDisplay: !!clientData.loyaltyPoints,
    disabled: !isExpertPlan,
    disabledMessage: intl.formatMessage({
      defaultMessage: 'Dostupné v plánu EXPERT. Aktivujte ve vašem profilu.',
      id: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    }),
  },
];

type CaptionCardConfig = {
  id: string;
  title: string;
  caption: string;
  endElement?: ReactNode;
  shouldDisplay?: boolean;
  editable?: boolean;
  fieldName?: keyof ClientWithVisitsWithVisitServices;
  disabled?: boolean;
  disabledMessage?: string;
};

const getCaptionCardConfigs = (
  intl: IntlShape,
  clientData: ClientWithVisitsWithVisitServices,
  allowedServices: Service[],
  changeClientDepositStatus: (
    variables: PartialClient,
    options?: MutateOptions<PartialClient, Error, PartialClient, unknown>,
  ) => void,
  isExpertPlan: boolean,
): CaptionCardConfig[] => [
  {
    id: 'depositRequirement',
    title: intl.formatMessage({
      defaultMessage: 'Platí zálohy',
      id: 'clientProfile.depositRequirement',
    }),
    caption: intl.formatMessage({
      defaultMessage:
        'Tímto od klienta vyžadujete zaplacení zálohy, kterou musí uhradit předem před objednanou službou.',
      id: 'clientProfile.depositRequirement.helperText',
    }),
    editable: false,
    endElement: (
      <Switch
        checked={clientData.deposit}
        onSubmitEndpoint={checked => {
          changeClientDepositStatus({ deposit: checked, id: clientData.id });
        }}
      />
    ),
  },
  {
    id: 'clientInfo',
    title: intl.formatMessage({
      id: 'clientProfile.clientInfo',
    }),
    caption:
      clientData.note ??
      intl.formatMessage({
        id: 'clientProfile.clientInfo.empty',
      }),
    fieldName: 'note',
    editable: true,
  },
  {
    id: 'allowedServices',
    endElement: !isExpertPlan ? (
      <Chip label="EXPERT" size="small" color="primary" variant="outlined" />
    ) : undefined,
    title: intl.formatMessage({
      id: 'clientProfile.reservationServices',
    }),
    caption: `${allowedServices?.map(service => service.serviceName.trim()).join(' | ')}`,
    editable: true,
    fieldName: 'allowedServices',
    disabled: !isExpertPlan,
    disabledMessage: intl.formatMessage({
      defaultMessage: 'Dostupné v plánu EXPERT. Aktivujte ve vašem profilu.',
      id: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
    }),
  },
];
